// src/components/LoadingSpinner.tsx
import React, { useState, useEffect, useRef } from 'react';
import { type ClassNameValue, twMerge } from 'tailwind-merge';

interface LoadingSpinnerProps {
  isStreaming: boolean;
  maxSnippetLength?: number;
  thinkingContent?: string;
  className?: ClassNameValue;
}

/**
 * Enhanced loading spinner with modern design and clean thinking content display.
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  isStreaming,
  maxSnippetLength = 120,
  thinkingContent = '',
  className
}) => {
  const [animationKey, setAnimationKey] = useState(0);
  const previousSnippetRef = useRef('');
  
  // Extract and clean thinking content, showing only the latest/newest thinking block
  const thinkingSnippet = React.useMemo(() => {
    if (!thinkingContent) return '';
    
    // Split thinking content by newlines to get individual thinking messages
    const thinkingLines = thinkingContent.split('\n').filter(line => line.trim());
    
    if (thinkingLines.length === 0) return '';
    
    // Look for the most recent substantive thinking (work backwards from the end)
    let latestThinking = '';
    for (let i = thinkingLines.length - 1; i >= 0; i--) {
      const line = thinkingLines[i].trim();
      
      // Skip very short or generic lines
      if (line.length < 10) continue;
      
      // Skip lines that are just punctuation or generic status
      if (line.match(/^[.\s!?-]+$/) || 
          line.match(/^(thinking|processing|analyzing|loading|waiting)\.{0,3}$/i)) {
        continue;
      }
      
      latestThinking = line;
      break;
    }
    
    // If no good line found, use the very last line
    if (!latestThinking) {
      latestThinking = thinkingLines[thinkingLines.length - 1];
    }
    
    // Comprehensive HTML and markdown cleanup for the latest thinking only
    const cleanContent = latestThinking
      // Remove HTML tags completely
      .replace(/<[^>]*>/g, '')
      // Remove markdown formatting
      .replace(/```[\s\S]*?```/g, '[code]')  // Replace code blocks
      .replace(/`([^`]+)`/g, '$1')           // Remove inline code ticks
      .replace(/\*\*([^*]+)\*\*/g, '$1')     // Remove bold markers
      .replace(/\*([^*]+)\*/g, '$1')         // Remove italic markers
      .replace(/__([^_]+)__/g, '$1')         // Remove bold underscores
      .replace(/_([^_]+)_/g, '$1')           // Remove italic underscores
      .replace(/#{1,6}\s+/g, '')             // Remove heading markers
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove markdown links
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1') // Remove images
      .replace(/^\s*[-*+]\s+/gm, '')         // Remove list markers
      .replace(/^\s*\d+\.\s+/gm, '')         // Remove numbered list markers
      .replace(/\s+/g, ' ')                  // Normalize whitespace
      .trim();
    
    return cleanContent;
  }, [thinkingContent]);

  // Reset animation when thinking snippet changes to show new content immediately
  useEffect(() => {
    if (thinkingSnippet !== previousSnippetRef.current && thinkingSnippet.length > 80) {
      setAnimationKey(prev => prev + 1);
      previousSnippetRef.current = thinkingSnippet;
    }
  }, [thinkingSnippet]);

  if (!isStreaming) {
    return null;
  }
  
  return (
    <div className={twMerge("flex items-center justify-start py-3 px-4", className)}>
      <div className="flex items-center gap-3 max-w-4xl w-full">
        {/* Aitana-inspired pixelated spinner - simplified and more visible */}
        <div className="relative w-6 h-6 flex-shrink-0">
          {/* Simple rotating squares that form a pattern */}
          <div className="absolute w-2 h-2 bg-[#FF9966] rounded-sm animate-pulse" 
               style={{ top: '0px', left: '0px', animationDelay: '0s', animationDuration: '1.5s' }}></div>
          <div className="absolute w-2 h-2 bg-[#FFB088] rounded-sm animate-pulse" 
               style={{ top: '0px', right: '0px', animationDelay: '0.2s', animationDuration: '1.5s' }}></div>
          <div className="absolute w-2 h-2 bg-[#FFB088] rounded-sm animate-pulse" 
               style={{ bottom: '0px', left: '0px', animationDelay: '0.4s', animationDuration: '1.5s' }}></div>
          <div className="absolute w-2 h-2 bg-[#FF9966] rounded-sm animate-pulse" 
               style={{ bottom: '0px', right: '0px', animationDelay: '0.6s', animationDuration: '1.5s' }}></div>
          
          {/* Center circle that pulses */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-[#FF9966] rounded-full animate-ping"
               style={{ animationDuration: '2s' }}></div>
        </div>
        
        {/* Single line thinking content display with scrolling animation */}
        <div className="flex-1 min-w-0 text-sm">
          {thinkingSnippet ? (
            <div className="flex items-center gap-2">
              <span className="text-gray-400 font-normal flex-shrink-0">Thinking:</span>
              <div className="flex-1 min-w-0 overflow-hidden">
                {thinkingSnippet.length > 80 ? (
                  <div 
                    key={animationKey}
                    className="whitespace-nowrap text-gray-500"
                    style={{
                      animation: 'marquee 8s linear infinite'
                    }}
                  >
                    {thinkingSnippet}
                  </div>
                ) : (
                  <span className="text-gray-500">{thinkingSnippet}</span>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center">
              <span className="text-gray-400 font-normal">Processing</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoadingSpinner;